import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import styled from '@emotion/styled'
import { Button } from '@mobi/component-library/Common/V2'
import { colors, font, spacing } from '@mobi/component-library/Theme/Common'
import { startCognitoAttributeVerification } from '../Hooks/useMfaVerification'
import { MFAMethod, MfaError } from '@mobi/api-types/src/mfa'
import { mapMFAMethodToAttributeName } from '../utils'
import { handleMfaError } from '../helpers'
import { MFASelectionItem } from './MFASelectionItem'
import { useMfaUpdateAuthMutation } from '../Hooks/useMfaUpdateAuthMutation'
import { clearVerificationCodePageSessionState } from '../Pages'
import { LoadingSpinner } from '@mobi/component-library/Feedback/LoadingSpinner'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import { PasswordVerification } from './PasswordVerification'
import { CognitoUnauthorizedError } from '../Hooks/useCognitoUserIsAuthenticated'
import { useCognitoUser } from '@core/Data/Account/useCognitoUser'

export const MFASelectionScreen: React.FC<{ accountNumber: number }> = ({ accountNumber }) => {
  const navigate = useNavigate()

  const { mutateAsync: updateMfaPreference } = useMfaUpdateAuthMutation()

  const onVerificationCompleteRef = useRef<(() => void) | (() => Promise<void>) | undefined>()
  const [isPasswordTrayOpen, setIsPasswordTrayOpen] = useState(false)
  const { data: cognitoUser, isLoading } = useCognitoUser(accountNumber)
  const activeMethod = cognitoUser?.preferredFactor || 'None'

  function navigateToEditContact() {
    navigate('/account/contactdetails')
  }

  function navigateToVerifyCode(preferredFactor: MFAMethod) {
    navigate(`/settings/mfa/verify-code/${preferredFactor}`)
  }

  function openVerifyPasswordTray(mfaMethod: MFAMethod) {
    setIsPasswordTrayOpen(true)
    onVerificationCompleteRef.current = () => onToggle(mfaMethod)
  }

  function closeVerifyPasswordTray() {
    setIsPasswordTrayOpen(false)
    onVerificationCompleteRef.current = undefined
  }

  useEffect(() => {
    // the verification page persists some state in case
    // the user leaves and comes back, but going to the
    // mfa settings page should reset this
    clearVerificationCodePageSessionState()
  }, [])

  const handleVerificationError = (error: MfaError) => {
    handleMfaError(error, 'verification')
  }

  const handleMethodSelection = async (mfaMethod: MFAMethod) => {
    try {
      await updateMfaPreference({ preferredFactor: mfaMethod })
    } catch (error) {
      if (error instanceof CognitoUnauthorizedError) {
        openVerifyPasswordTray(mfaMethod)
      } else {
        handleMfaError(error as MfaError, 'update')
      }
    }
  }

  const onToggle = async (mfaMethod: MFAMethod) => {
    const needsVerification =
      (mfaMethod === 'SMS' && cognitoUser?.userAttributes.phone_number_verified === 'false') ||
      (mfaMethod === 'Email' && cognitoUser?.userAttributes.email_verified === 'false')

    const newMethod = activeMethod === mfaMethod ? 'None' : mfaMethod

    if (needsVerification && newMethod !== 'None') {
      try {
        await startCognitoAttributeVerification({
          attributeName: mapMFAMethodToAttributeName(newMethod),
        })
        navigateToVerifyCode(mfaMethod)
      } catch (error) {
        if (error instanceof CognitoUnauthorizedError) {
          openVerifyPasswordTray(mfaMethod)
        } else {
          handleVerificationError(error as MfaError)
        }
      }
    } else {
      await handleMethodSelection(newMethod)
    }
  }

  if (isLoading) {
    return (
      <ContainerStyled>
        <LoadingSpinner color='purple' size='1.8rem' />
      </ContainerStyled>
    )
  }

  if (cognitoUser === undefined) {
    return (
      <ContainerStyled>
        <p>There was an error fetching your Multi-factor authentication (MFA) details.</p>
      </ContainerStyled>
    )
  }

  return (
    <ContainerStyled data-tid-mfa-selection-screen>
      <MFASelectionItem
        iconName='LinePhoneCall01'
        mfaMethod='SMS'
        isVerified={cognitoUser.userAttributes.phone_number_verified === 'true'}
        id='mfa-phone-verification'
        isToggled={activeMethod === 'SMS'}
        contactInfo={cognitoUser.userAttributes.phone_number}
        onToggle={() => onToggle('SMS')}
      />
      <MFASelectionItem
        id='mfa-email-verification'
        iconName='LineMail02'
        mfaMethod='Email'
        isVerified={cognitoUser.userAttributes.email_verified === 'true'}
        isToggled={activeMethod === 'Email'}
        contactInfo={cognitoUser.userAttributes.email}
        onToggle={() => onToggle('Email')}
      />

      <ButtonStyled
        onClick={() => navigateToEditContact()}
        color='secondary_color'
        data-tid-edit-contact
      >
        Edit contact details
      </ButtonStyled>

      {accountNumber ? (
        <Tray
          isOpen={isPasswordTrayOpen}
          headerTitle='Verify Account'
          onDismiss={() => closeVerifyPasswordTray()}
        >
          <PasswordVerification
            onVerify={() => {
              const fn = onVerificationCompleteRef.current
              fn && fn()
              closeVerifyPasswordTray()
            }}
            onCancel={() => closeVerifyPasswordTray()}
            accountNumber={accountNumber}
          />
        </Tray>
      ) : null}
    </ContainerStyled>
  )
}

// ======
// Styles
// ======

const ContainerStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  padding: spacing.md,
  gap: spacing.md,
})

// todo: make a secondary button component in the component lib
const ButtonStyled = styled(Button)({
  alignSelf: 'center',
  borderRadius: '6px',
  marginTop: spacing.sm,
  background: colors.lavender[50],
  border: `1px solid ${colors.lavender[50]}`,
  color: colors.lavender[700],
  ...font.size.lg,
  width: '50%',
})
